import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Spinner, Chip, Input, Avatar } from "@heroui/react";
import { Typography, UniversalTable, IdView } from "../../../../../components";
import { useTranslations } from "../../../../../contexts/translation.context";
import { EmptyContentIc, CalendarIcon, DoubleArrowRightIc } from "../../../../../components/icons";
import { TableColumn } from "../../../../../types/account";
import { staffApi, BalanceHistoryTransaction } from "../../../../../api/staff";

type WalletActionType =
  | 'balance_top_up'
  | 'withdrawal'
  | 'refund'
  | 'balance_change';

interface BalanceHistoryTabProps {
  partnerId: string;
}

const SEARCH_DEBOUNCE_DELAY = 500;

// Вспомогательные функции
const extractHandleFromLink = (link?: string): string | undefined => {
  if (!link) return undefined;

  try {
    const url = new URL(link);
    const [firstSegment] = url.pathname.split('/').filter(Boolean);
    if (firstSegment) {
      return firstSegment.startsWith('@') ? firstSegment : `@${firstSegment}`;
    }
  } catch {
    const match = link.match(/onlyfans\.com\/([^/?#]+)/i);
    if (match?.[1]) {
      const username = match[1];
      return username.startsWith('@') ? username : `@${username}`;
    }
  }

  return undefined;
};

const pickFirstString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

/**
 * BalanceHistoryTab Component
 *
 * Таб с историей баланса партнера.
 * Показывает таблицу транзакций с изменениями баланса.
 * Полностью основана на структуре из onlyu_front/src/partner/account/Wallet.tsx
 */
const BalanceHistoryTab: React.FC<BalanceHistoryTabProps> = ({ partnerId }) => {
  const { t } = useTranslations();
  const [transactions, setTransactions] = useState<BalanceHistoryTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchId, setSearchId] = useState('');
  const [localSearchId, setLocalSearchId] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchId(localSearchId);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearchId]);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return localSearchId !== '';
  }, [localSearchId]);

  // API params
  const apiParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      page_size: pageSize,
    };

    if (searchId) {
      params.id = searchId;
    }

    return params;
  }, [currentPage, pageSize, searchId]);

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await staffApi.getPartnerBalanceHistory(partnerId, apiParams);
      setTransactions(response.objects);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, apiParams]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const columns: TableColumn[] = [
    { key: "id", name: t('wallet.id') },
    { key: "amount", name: t('wallet.amount') },
    { key: "action", name: t('wallet.action') },
    { key: "date", name: t('wallet.date') }
  ];

  const formatShortId = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.length <= 4) return trimmed;
    return trimmed.slice(-4);
  };

  const formatDate = (dateString: string) => {
    const utcDate = new Date(dateString);
    if (Number.isNaN(utcDate.getTime())) {
      return '';
    }
    const date = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.max(0, Math.floor(diffInMs / (1000 * 60 * 60 * 24)));
    const diffInHours = Math.max(0, Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));

    if (diffInDays > 0) {
      return t('wallet.timeAgo.daysHours', { days: diffInDays, hours: diffInHours });
    }
    if (diffInHours > 0) {
      return t('wallet.timeAgo.hours', { hours: diffInHours });
    }
    const diffInMinutes = Math.max(0, Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60)));
    return t('wallet.timeAgo.minutes', { minutes: diffInMinutes });
  };

  const getAmountColor = (amount: number) => {
    return amount >= 0 ? 'text-[#19BE60]' : 'text-[#F31260]';
  };

  const getActionType = (transaction: BalanceHistoryTransaction): WalletActionType => {
    const amount = transaction.amount;

    // Если есть транзакция
    if (transaction.transaction) {
      return amount >= 0 ? 'balance_top_up' : 'withdrawal';
    }

    // Если есть инвойс
    if (transaction.invoice) {
      const status = transaction.invoice.status?.toLowerCase();

      // Положительная сумма
      if (amount >= 0) {
        // Если статус refunded - это возврат средств
        if (status === 'refunded') {
          return 'refund';
        }
        // Иначе - пополнение баланса
        return 'balance_top_up';
      }

      // Отрицательная сумма - вывод средств
      return 'withdrawal';
    }

    // По умолчанию определяем по знаку суммы
    return amount >= 0 ? 'balance_top_up' : 'withdrawal';
  };

  const getStatusChip = (actionType: WalletActionType) => {
    const config: Record<WalletActionType, { text: string; color: string }> = {
      balance_top_up: { text: t('wallet.balanceTopUpAction') || 'Balance Top Up', color: '#F5A524' },
      withdrawal: { text: t('wallet.withdrawal.action') || 'Withdrawal', color: '#F31260' },
      refund: { text: t('wallet.refund') || 'Refund', color: '#17C964' },
      balance_change: { text: t('wallet.balanceChange') || 'Balance Change', color: '#7828C8' }
    };

    return config[actionType] ?? config.balance_change;
  };

  const renderCell = useCallback((transaction: BalanceHistoryTransaction, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return (
          <IdView textToCopy={transaction.id}>
            {formatShortId(transaction.id) ?? transaction.id}
          </IdView>
        );
      case "amount": {
        const isPositive = transaction.amount >= 0;
        const sign = isPositive ? '+' : '';
        const formattedAmount = Number.isFinite(transaction.amount) ? Math.abs(transaction.amount).toFixed(2) : '0.00';
        const formattedBefore = Number.isFinite(transaction.before) ? transaction.before.toFixed(2) : '0.00';
        const formattedAfter = Number.isFinite(transaction.after) ? transaction.after.toFixed(2) : '0.00';

        return (
          <div className="flex flex-col gap-[4px]">
            <span className={`${getAmountColor(transaction.amount)} text-[14px] font-semibold`}>
              {sign}${formattedAmount}
            </span>
            <div className="flex items-center gap-[4px]">
              <span className="text-[#71717A] text-[14px] font-normal">${formattedBefore}</span>
              <DoubleArrowRightIc />
              <span className="text-[#11181C] text-[14px] font-semibold">${formattedAfter}</span>
            </div>
          </div>
        );
      }
      case "action": {
        const actionType = getActionType(transaction);
        const statusChip = getStatusChip(actionType);

        // Получаем данные модели (креэйтора) для партнеров
        const modelUsername = (transaction as any)?.model?.username;
        const modelHandle = modelUsername && typeof modelUsername === 'string' && modelUsername.trim()
          ? (modelUsername.trim().startsWith('@') ? modelUsername.trim() : `@${modelUsername.trim()}`)
          : extractHandleFromLink(
              transaction.company?.link ||
              (transaction.invoice as any)?.company?.link ||
              (transaction.invoice as any)?.campaign?.link
            );

        const modelAvatar = (transaction as any)?.model?.avatar;
        const modelLink = pickFirstString(
          (transaction as any)?.model?.link,
          (transaction.invoice as any)?.model?.link,
          (transaction.invoice as any)?.company?.link,
          (transaction.invoice as any)?.campaign?.link,
          transaction.company?.link
        );

        const handleWithoutAt = modelHandle?.startsWith('@') ? modelHandle.slice(1) : modelHandle;
        const modelHref = modelLink || (handleWithoutAt ? `https://onlyfans.com/${handleWithoutAt}` : undefined);
        const modelLabel = modelHandle;
        const shouldShowModel = Boolean(modelLabel);
        const showAvatar = Boolean(modelAvatar && shouldShowModel);

        const hasInvoice = Boolean(transaction.invoice?.id);
        const hasCompany = Boolean(!hasInvoice && transaction.company?.id);
        const showOrderSection = hasInvoice || hasCompany;

        return (
          <div className="flex flex-col gap-[4px]">
            <Chip
              style={{
                backgroundColor: `${statusChip.color}20`,
                color: statusChip.color
              }}
              className="h-[24px] px-[12px] py-[4px] text-[12px] font-medium w-fit whitespace-nowrap"
            >
              {statusChip.text}
            </Chip>
            {/* Показываем ID транзакции для пополнения баланса */}
            {actionType === 'balance_top_up' && transaction.transaction?.admin && (
              <div className="flex flex-wrap items-center gap-[8px]">
                <span className="text-[#11181C] text-[14px] font-semibold">
                  {t('wallet.paymentTitle') || 'Payment'}
                </span>
                <IdView textToCopy={transaction.transaction.admin}>
                  {formatShortId(transaction.transaction.admin) ?? transaction.transaction.admin}
                </IdView>
              </div>
            )}
            {/* Показываем Invoice ID с моделью или Order ID с моделью */}
            {showOrderSection && (
              <div className="flex flex-wrap items-center gap-[8px]">
                {hasInvoice ? (
                  <div className="flex items-center gap-[6px]">
                    <span className="text-[#11181C] text-[14px] font-semibold">
                      {t('wallet.invoiceTitle') || 'Invoice'}
                    </span>
                    <IdView textToCopy={transaction.invoice!.id}>
                      {formatShortId(transaction.invoice!.id) ?? transaction.invoice!.id}
                    </IdView>
                  </div>
                ) : hasCompany ? (
                  <div className="flex items-center gap-[6px]">
                    <span className="text-[#11181C] text-[14px] font-semibold">
                      {t('wallet.orderTitle') || 'Order'}
                    </span>
                    <IdView textToCopy={transaction.company!.id}>
                      {formatShortId(transaction.company!.id) ?? transaction.company!.id}
                    </IdView>
                  </div>
                ) : null}
                {showAvatar && (
                  <Avatar
                    src={modelAvatar}
                    size="sm"
                    showFallback
                    name={modelLabel?.slice(1, 2)}
                    className="w-[16px] h-[16px] rounded-[4px]"
                  />
                )}
                {shouldShowModel && (
                  modelHref ? (
                    <a
                      href={modelHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#006FEE] text-[14px] font-normal"
                    >
                      {modelLabel}
                    </a>
                  ) : (
                    <span className="text-[#71717A] text-[14px] font-normal">
                      {modelLabel}
                    </span>
                  )
                )}
              </div>
            )}
          </div>
        );
      }
      case "date":
        return (
          <div className="flex items-center gap-[8px]">
            <CalendarIcon />
            <span className="text-[#71717A] text-[12px]">{formatDate(transaction.created_at)}</span>
          </div>
        );
      default:
        return null;
    }
  }, [t]);

  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc />
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {hasActiveFilters
              ? (t('wallet.emptyHistoryFilteredTitle') || 'По выбранным фильтрам нет транзакций')
              : (t('wallet.emptyHistoryTitle') || 'История транзакций пуста')
            }
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {hasActiveFilters
              ? (t('wallet.emptyHistoryFilteredDescription') || 'Попробуйте изменить параметры фильтров')
              : (t('wallet.emptyHistoryDescription') || 'Транзакции появятся здесь после первых операций')
            }
          </Typography>
        </div>
      </div>
    </div>
  );

  if (isLoading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[24px] w-full">
      {/* Title */}
      <Typography variant="h6" className="text-[#11181C] font-medium text-[20px] leading-[24px]">
        {t('wallet.balanceHistory')}
      </Typography>

      {/* Search by ID */}
      <Input
        type="text"
        placeholder={t('wallet.searchById') || 'Поиск по ID'}
        value={localSearchId}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearchId(e.target.value)}
        endContent={
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17.5 17.5L13.875 13.875" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        classNames={{
          mainWrapper: "w-[273px]",
          base: "w-[273px]",
          input: "text-sm",
          inputWrapper: "rounded-lg h-[36px] w-[273px]",
        }}
      />

      {/* Table */}
      <div className="flex-1">
        <UniversalTable
          data={transactions}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          currentPage={currentPage}
          currentPageSize={pageSize}
        />
      </div>
    </div>
  );
};

export default BalanceHistoryTab;
